// ...existing code...
import * as React from 'react';
import { useRecordContext } from 'react-admin';

import {
  Divider,
  makeStyles,
  Menu,
  MenuItem,
  useMediaQuery,
} from '@material-ui/core';
import IconButton from '@material-ui/core/IconButton';
import { useTheme } from '@material-ui/core/styles';
import Tooltip from '@material-ui/core/Tooltip';
import MoreVertIcon from '@material-ui/icons/MoreVert';

const useStyles = makeStyles((theme) => ({
  root: {
    /* estilos globais do botão (opcional) */
    [theme.breakpoints.down('sm')]: {
      padding: 4,
      '& .MuiSvgIcon-root': {
        fontSize: '1.125rem',
      },
    },
  },
  menuItemMobile: {
    minHeight: 28,
    paddingTop: 4,
    paddingBottom: 4,
    paddingLeft: 10,
    paddingRight: 10,
    fontSize: '0.75rem',
    '& .MuiSvgIcon-root': {
      marginRight: 8,
      fontSize: '1.125rem',
    },
  },
}));

/**
 * Resolve os campos dinâmicos (show/disabled/title/icon, que podem ser valor fixo ou
 * função (record) => valor) de uma action em relação ao record atual.
 */
export const resolverAction = (action, record) => {
  const show =
    typeof action.show === 'function'
      ? action.show(record)
      : action.show !== undefined
      ? !!action.show
      : true;

  const disabled =
    typeof action.disabled === 'function'
      ? action.disabled(record)
      : !!action.disabled;

  const Icon = action.icon;
  const iconNode = React.isValidElement(Icon) ? (
    Icon
  ) : typeof Icon === 'function' ? (
    <Icon record={record} />
  ) : null;

  const title =
    typeof action.title === 'function'
      ? action.title(record)
      : action.title !== undefined
      ? action.title
      : '-';

  return { show, disabled, iconNode, title };
};

const renderBotaoAction = (action, idx, record, classes) => {
  const key = action.key || idx;
  const vazio = action.vazio ? action.vazio : null;
  const { show, disabled, iconNode, title } = resolverAction(action, record);
  if (!show) return vazio;

  const handleClick = (e) => {
    if (typeof action.onClick === 'function') {
      action.onClick(record, e);
    }
  };

  // 🔥 COMPONENTE DINÂMICO
  const Component = action.component || IconButton;

  const propsButton = action.propsButton ? action.propsButton : {};

  return (
    <Tooltip
      key={key}
      title={title}
      placement={action.placement || 'bottom'}
      aria-label={action.ariaLabel || action.title || `action-${key}`}
    >
      <Component
        aria-label={action.ariaLabel || title || `action-${key}`}
        classes={{ root: classes.root }}
        size="small"
        onClick={handleClick}
        disabled={disabled}
        {...propsButton}
      >
        {iconNode}
      </Component>
    </Tooltip>
  );
};

/**
 * Botão de "mais ações" (3 pontinhos verticais) + menu com as ações que não couberam
 * nos botões visíveis (ver prop `maxAcoesVisiveis` de ActionsField).
 */
const MenuMaisAcoesField = ({ acoes, record }) => {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const classes = useStyles();
  const theme = useTheme();
  // no mobile, o menu abre colado embaixo do ícone (em vez de sobrepor ele) e os itens
  // ficam mais compactos, com divisor entre eles.
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const itensValidos = acoes
    .map((action, idx) => {
      const key = action.key || idx;
      const { show, disabled, iconNode, title } = resolverAction(
        action,
        record,
      );
      return show ? { key, action, disabled, iconNode, title } : null;
    })
    .filter(Boolean);

  return (
    <>
      <Tooltip title="Mais ações">
        <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)}>
          <MoreVertIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        {...(isMobile
          ? {
              anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
              getContentAnchorEl: null,
            }
          : {})}
      >
        {itensValidos.map(({ key, action, disabled, iconNode, title }, i) => [
          i > 0 && <Divider key={`${key}-divisor`} />,
          <MenuItem
            key={key}
            dense={isMobile}
            disabled={disabled}
            className={isMobile ? classes.menuItemMobile : ''}
            onClick={(e) => {
              setAnchorEl(null);
              if (typeof action.onClick === 'function') {
                action.onClick(record, e);
              }
            }}
          >
            {iconNode}
            {title}
          </MenuItem>,
        ])}
      </Menu>
    </>
  );
};

/**
 * ActionsField genérico
 * Props:
 * - actions: Array de ações { key?, title, icon, onClick, disabled?, show?, placement?, ariaLabel? }
 *    - icon: componente React (ex: EditOutlinedIcon) ou elemento JSX (<EditOutlinedIcon />)
 *    - onClick: função (record, event) => void
 *    - disabled: boolean | (record) => boolean
 *    - show: boolean | (record) => boolean
 * - maxAcoesVisiveis: quando informado e o número de ações com `show` verdadeiro exceder
 *   esse limite, as primeiras ficam como botões e o restante é agrupado num menu "mais
 *   ações" (3 pontinhos verticais). Sem essa prop, mostra todas as ações como botões, igual
 *   ao comportamento de sempre (usado por todas as outras telas que reaproveitam este
 *   componente sem essa prop).
 * - qualquer outro prop é passado para useRecordContext (compatibilidade com react-admin)
 *
 * Exemplo de action:
 * { key: 'edit', title: 'Editar', icon: EditOutlinedIcon, onClick: (rec) => handleEdit(rec), disabled: (rec) => rec.id === 1 }
 */
const ActionsField = ({ actions = [], maxAcoesVisiveis, ...rest }) => {
  const classes = useStyles();
  const ctx = useRecordContext(rest);
  const recordWrapper = ctx && ctx.record ? ctx.record : ctx;
  const record = recordWrapper || {};

  const listaAcoes = Array.isArray(actions) ? actions : [];

  if (typeof maxAcoesVisiveis !== 'number') {
    return (
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        {listaAcoes.map((action, idx) =>
          renderBotaoAction(action, idx, record, classes),
        )}
      </div>
    );
  }

  // com `maxAcoesVisiveis`, o limite considera só as ações que passam pelo `show` atual
  // (o `vazio` de uma ação oculta deixa de ser renderizado nesse modo).
  const acoesExibiveis = listaAcoes.filter(
    (action) => resolverAction(action, record).show,
  );
  const temLimite = acoesExibiveis.length > maxAcoesVisiveis;
  const acoesVisiveis = temLimite
    ? acoesExibiveis.slice(0, maxAcoesVisiveis)
    : acoesExibiveis;
  const acoesNoMenu = temLimite ? acoesExibiveis.slice(maxAcoesVisiveis) : [];

  return (
    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
      {acoesVisiveis.map((action, idx) =>
        renderBotaoAction(action, idx, record, classes),
      )}
      {acoesNoMenu.length > 0 && (
        <MenuMaisAcoesField acoes={acoesNoMenu} record={record} />
      )}
    </div>
  );
};

export default ActionsField;
// ...existing code...
